import { ChangeEvent, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons/faChevronRight";
import { ButtonWithIcon } from "../button-with-icon/button-with-icon";
import { resources } from "@constants/hardcoded-strings";
import { Table } from "@tanstack/react-table";
import styles from "./pagination-container.less";

type PaginationContainerProps<T> = {
    isPageHeight?: boolean;
    useTableResult: Table<T>,
    idPrefix: string;
};

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50, 100];

export const PaginationContainer = <T extends object>(props: PaginationContainerProps<T>) => {

    const {
        useTableResult,
        idPrefix = '',
        isPageHeight
    } = props;

    const {
        setPageIndex,
        setPageSize,
        nextPage,
        previousPage,
        firstPage,
        lastPage,
        getPageCount,
        getCanNextPage,
        getCanPreviousPage,
        getState,
        getRowCount,
    } = useTableResult;

    const tableState = getState();
    // local tracker used to just update text in input
    const pageIndex = tableState.pagination.pageIndex;
    const pageSize = tableState.pagination.pageSize;
    const rowCount = getRowCount();
    const pageCount = getPageCount();
    const canNextPage = getCanNextPage();
    const canPreviousPage = getCanPreviousPage();

    const [pageIndexState, setPageIndexState] = useState(tableState.pagination.pageIndex);

    // tracker to update local state when page index changes
    useEffect(() => {
        if (pageIndex !== pageIndexState) {
            setPageIndexState(pageIndex);
        }
    }, [pageIndex]);
    const goToFirst = () => {
        firstPage();
        setPageIndexState(0);
    }

    const goToLast = () => {
        lastPage();
        setPageIndexState(pageCount - 1);
    }

    const goToNextPage = () => {
        nextPage();
        setPageIndexState(pageIndex + 1);
    }

    const goToPrevPage = () => {
        previousPage();
        setPageIndexState(pageIndex - 1);
    }

    const onChangePageNumber = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.value) {
            const page = event.target.value ? Number(event.target.value) - 1 : 0;
            if (page < pageCount && page >= 0) {
                setPageIndex(page);
                setPageIndexState(page);
            }
        } else {
            setPageIndexState(null);
        }
    };

    const setPageSizeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (event?.target?.value) {
            goToFirst();
            setPageSize(Number(event.target.value));
        }
    }

    const resultsText = useMemo(() => {
        const startingRowIndex = rowCount > 0 ? pageIndex * pageSize + 1 : 0;
        const endingRowIndex = (pageSize * (pageIndex + 1)) > rowCount ? rowCount : (pageSize * (pageIndex + 1));
        return resources.pagination.results(startingRowIndex, endingRowIndex, rowCount);
    }, [pageIndex, pageSize, rowCount]);

    const currentPageIndex = useMemo(() => {
        if (rowCount > 0) {
            return pageIndexState !== null ? (pageIndexState + 1).toString() : "";
        } else {
            return 0;
        }
    }, [pageIndexState, rowCount]);

    return rowCount > 0 ? (
        <div className={classNames(styles.paginationContainer, isPageHeight ? styles.pageHeightPagination : "")}>
            <div className={styles.resultsDiv}>
                <span>
                    {resultsText}
                </span>
            </div>
            <div className={styles.limitDiv}>
                <span className={styles.limitText}>{resources.pagination.limit}</span>
                <select
                    title={"Page size"}
                    value={pageSize}
                    onChange={setPageSizeHandler as any}
                >
                    {PAGE_SIZE_OPTIONS.map(pageSize => (
                        <option key={pageSize} id={`page-size-${pageSize}-option`} value={pageSize}>
                            {pageSize}
                        </option>
                    ))}
                </select>
            </div>
            <Button id={`${idPrefix}-pagination-first`} variant={"outline-primary"} disabled={!canPreviousPage} onClick={goToFirst}>
                {resources.pagination.first}
            </Button>
            <ButtonWithIcon
                id={`${idPrefix}-pagination-previous-page`}
                variant={"outline-primary"}
                icon={faChevronLeft}
                title={resources.pagination.previousPage}
                onClick={goToPrevPage}
                disabled={!canPreviousPage}
            />
            <div className={styles.pageDiv}>
                <Form.Control
                    id={`${idPrefix}-pagination-custom-page`}
                    type="number"
                    title={"Page number"}
                    value={currentPageIndex}
                    onChange={onChangePageNumber}
                    disabled={!canPreviousPage && !canNextPage}
                />
                <span> / {pageCount}</span>
            </div>
            <ButtonWithIcon
                id={`${idPrefix}-pagination-next-page`}
                variant={"outline-primary"}
                icon={faChevronRight}
                title={resources.pagination.nextPage}
                onClick={goToNextPage}
                disabled={!canNextPage}
            />
            <Button id={`${idPrefix}-pagination-last`} variant={"outline-primary"} disabled={!canNextPage} onClick={goToLast}>
                {resources.pagination.last}
            </Button>
        </div>
    ) : null;
};